import React, { useState, useEffect, use } from 'react';
import {
  Button,
  Box,
  Checkbox,
  Link,
  Typography,
  Grid,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import Loader from '../../../../components/common/Loader';
import companyProfileRequest from '../../../../requests/CompanyProfileRequest';
import formattedMessages from '../../../../messages/account/CompanyProfileMessages';

const CampaignView = ({
  campaignCode,
  accessLevel,
  accessType,
  errorHandler,
}) => {
  const { getCampaignDetails, downloadAttachments } = companyProfileRequest();
  const [campaign, setCampaign] = useState({});
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCampaignDetails = async () => {
      setLoading(true);
      try {
        const response = await getCampaignDetails(campaignCode, errorHandler);
        setCampaign(response);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [campaignCode, errorHandler]);

  const handleDownloadAttachments = async (attachmentName) => {
    await downloadAttachments(attachmentName, errorHandler);
  };

  const generateMessageDetails = () => {
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          {formattedMessages.messageDetails}
        </Typography>
        <Grid>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 150,
              }}
            >
              {formattedMessages.vertical}
            </Typography>
            <Typography>
              {campaign.vertical ? campaign.vertical : 'N/A'}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 150,
              }}
            >
              {formattedMessages.description}
            </Typography>
            <Typography>{campaign.description}</Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 150,
              }}
            >
              {formattedMessages.sampleMessages}
            </Typography>
            {generateSampleMessages()}
          </Stack>
        </Grid>
      </>
    );
  };

  const generateSampleMessages = () => {
    return (
      <Box
        sx={{
          border: '2px solid',
          borderColor: 'grey.300',
          borderRadius: '15px',
          overflow: 'hidden',
          maxWidth: '100%',
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <Typography variant="h6">
                  {formattedMessages.sample1}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {campaign.sample_1 ? campaign.sample_1 : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <Typography variant="h6">
                  {formattedMessages.sample2}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {campaign.sample_2 ? campaign.sample_2 : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <Typography variant="h6">
                  {formattedMessages.sample3}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {campaign.sample_3 ? campaign.sample_3 : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <Typography variant="h6">
                  {formattedMessages.sample4}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {campaign.sample_4 ? campaign.sample_4 : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ width: 100 }}>
                <Typography variant="h6">
                  {formattedMessages.sample5}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="h6">
                  {campaign.sample_5 ? campaign.sample_5 : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  const generateMessageAttributes = () => {
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          {formattedMessages.messageAttributes}
        </Typography>
        <Grid container columnSpacing={6}>
          {/* Left column - main contact info */}
          <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
            {/* Subscriber Opt-in Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.subscriberOptIn}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="subscriber_opt_in">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.subscriber_opt_in}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.subscriber_opt_in}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ ml: 1 }}>
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.keyWords}
              </Typography>
              <Typography variant="h6">
                {campaign.opt_in_keywords || 'N/A'}
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ ml: 1 }}
              marginBottom={2}
            >
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.message}
              </Typography>
              <Typography variant="h6">
                {campaign.opt_in_message || 'N/A'}
              </Typography>
            </Stack>

            {/* Subscriber Opt-out Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.subscriberOptOut}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="subscriber_opt_out">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.subscriber_opt_out}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.subscriber_opt_out}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ ml: 1 }}>
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.keyWords}
              </Typography>
              <Typography variant="h6">
                {campaign.opt_out_keywords || 'N/A'}
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ ml: 1 }}
              marginBottom={2}
            >
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.message}
              </Typography>
              <Typography variant="h6">
                {campaign.opt_out_message || 'N/A'}
              </Typography>
            </Stack>

            {/* Subscriber Help Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.subscriberHelp}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="subscriber_help">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.subscriber_help}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.subscriber_help}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ ml: 1 }}>
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.keyWords}
              </Typography>
              <Typography variant="h6">
                {campaign.help_keywords || 'N/A'}
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ ml: 1 }}
              marginBottom={2}
            >
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.message}
              </Typography>
              <Typography variant="h6">
                {campaign.help_message || 'N/A'}
              </Typography>
            </Stack>
          </Grid>

          {/* Right column - position and department */}
          <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
            {/* Embedded Link Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.embeddedLink}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="embedded_link">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.embedded_link === '1' ? true : false}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={campaign.embedded_link === '1' ? false : true}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ ml: 1 }}
              marginBottom={2}
            >
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.link}
              </Typography>
              <Typography variant="h6">
                {campaign.embedded_link_sample
                  ? campaign.embedded_link_sample
                  : 'N/A'}
              </Typography>
            </Stack>

            {/* Embedded Phone Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.embeddedPhone}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="embedded_phone">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.embedded_phone === '1' ? true : false}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={campaign.embedded_phone === '1' ? false : true}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              sx={{ ml: 1 }}
              marginBottom={2}
            >
              <Typography variant="h6" sx={{ width: 110, flexShrink: 0 }}>
                {formattedMessages.phoneNumber}
              </Typography>
              <Typography variant="h6">
                {campaign.embedded_phone_sample || 'N/A'}
              </Typography>
            </Stack>

            {/* Number Pool Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.numberPooling}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="number_pool">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.number_pool}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.number_pool}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>

            {/* Direct Lending Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.directLendingOrLoanArrangements}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="direct_lending">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.direct_lending}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.direct_lending}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
            {/* Affiliate Marketing Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.affiliateMarketing}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="affiliate_marketing">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.affiliate_marketing}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.affiliate_marketing}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>

            {/* Age-gated Content Field */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.ageGatedContent}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="age_gated">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.age_gated === '1' ? true : false}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.age_gated === '1' ? false : true}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  };

  const generateOptInDetails = () => {
    const { attachments } = campaign;
    let attachmentList = null;
    if (attachments && attachments.length > 0) {
      attachmentList = attachments.map((attachment) => (
        <Typography key={attachment.attachment_name}>
          <Link
            tabIndex={1}
            onClick={() =>
              handleDownloadAttachments(
                attachment.attachment_name,
                errorHandler
              )
            }
          >
            {attachment.attachment_name}
          </Link>
        </Typography>
      ));
    }

    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          {formattedMessages.optInDetails}
        </Typography>
        <Grid>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 170,
              }}
            >
              {formattedMessages.serviceAgreement}
            </Typography>
            <Typography>
              {campaign.terms_and_conditions_link ? (
                <Link>{campaign.terms_and_conditions_link}</Link>
              ) : (
                'N/A'
              )}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 170,
              }}
            >
              {formattedMessages.privacyPolicy}
            </Typography>
            <Typography>
              {campaign.privacy_policy_link ? (
                <Link>{campaign.privacy_policy_link}</Link>
              ) : (
                'N/A'
              )}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 170,
                flexShrink: 0,
              }}
            >
              {formattedMessages.optInMethod}
            </Typography>
            <Typography>
              {campaign.message_flow ? campaign.message_flow : 'N/A'}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
            alignItems={{ md: 'top' }}
          >
            <Typography
              sx={{
                width: 170,
              }}
            >
              {formattedMessages.providedAttachments}
            </Typography>
            <Stack direction="row">
              <Box>
                {attachmentList}
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ backgroundColor: 'common.white' }}
                  disabled={campaign.attachments?.length === 0}
                  onClick={() => handleDownloadAttachments(null, errorHandler)}
                >
                  {formattedMessages.downloadAll}
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Grid>
      </>
    );
  };

  const generateNumberList = () => {
    const { dedicatedNumbers } = campaign;
    if (!dedicatedNumbers || dedicatedNumbers.length === 0) {
      return <Typography>No result found.</Typography>;
    }
    const numberList = dedicatedNumbers.map((number) => (
      <TableRow key={number}>
        <TableCell>{number.reply_tn}</TableCell>
        <TableCell>{number.user_email}</TableCell>
      </TableRow>
    ));
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          {formattedMessages.associatedNumbers}
        </Typography>
        <Table>
          <TableHead>
            <TableCell>{formattedMessages.dedicatedPhoneNumber}</TableCell>
            <TableCell>{formattedMessages.user}</TableCell>
          </TableHead>
          <TableBody>{numberList}</TableBody>
        </Table>
      </>
    );
  };

  const generateProviderSpecific = () => {
    if (!(accessLevel === '3' && accessType === '3')) {
      return null;
    }
    return (
      <>
        <Typography variant="h2" gutterBottom sx={{ paddingTop: 4 }}>
          PROVIDER SPECIFIC
        </Typography>
        <Grid container columnSpacing={6}>
          {/* Left column - main contact info */}
          <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.CSP}
              </Typography>
              <Typography>{campaign.csp}</Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
              marginBottom={2}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.autoRenewal}
              </Typography>
              <Box sx={{ width: '300px' }}>
                <RadioGroup row name="auto_renewal">
                  <FormControlLabel
                    control={<Radio />}
                    checked={campaign.auto_renewal}
                    label="Yes"
                    sx={{ margin: 0 }}
                    disabled
                  />
                  <FormControlLabel
                    checked={!campaign.auto_renewal}
                    control={<Radio />}
                    label="No"
                    sx={{ margin: 0 }}
                    disabled
                  />
                </RadioGroup>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }} maxWidth={{ md: 450 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              sx={{ mb: 2 }}
            >
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.DCA}
              </Typography>
              <Typography>{campaign.csp}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems={{ md: 'center' }}>
              <Typography
                sx={{
                  width: 150,
                }}
              >
                {formattedMessages.mock}
              </Typography>
              <Checkbox checked={campaign.mock || false} disabled />
            </Stack>
          </Grid>
        </Grid>
        <Button variant="contained" color="secondary" sx={{ mt: 2 }}>
          {formattedMessages.edit}
        </Button>
      </>
    );
  };

  const generateContent = () => {
    if (Object.keys(campaign).length === 0) {
      return <Typography>No result found.</Typography>;
    }

    return (
      <Box>
        <Typography variant="h2" gutterBottom>
          {campaignCode}
        </Typography>
        <Grid>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 150,
              }}
            >
              {formattedMessages.campaignType}
            </Typography>
            <Typography>{campaign.use_case}</Typography>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                width: 150,
              }}
            >
              {formattedMessages.subCategory}
            </Typography>
            <Typography>{campaign.sub_use_cases || 'N/A'}</Typography>
          </Stack>
        </Grid>
        {/* Message Details Section */}
        {generateMessageDetails()}
        {/* Message Attributes Section */}
        {generateMessageAttributes()}
        {/* Opt-in Details Section */}
        {generateOptInDetails()}
        {/* Number List Section */}
        {generateNumberList()}
        {/* Provider Section */}
        {generateProviderSpecific()}
      </Box>
    );
  };

  return isLoading ? <Loader sx={{ alignItems: 'left' }} /> : generateContent();
};

export default CampaignView;
